import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailLink,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signOut,
  User as FirebaseUser
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { auth, functions } from '@/lib/firebase'
import { setTimezone, getTimezonePreference, getTimezone } from '@/lib/dateTime'
import i18n from '@/lib/i18n'
import type { User } from '@/types'

interface AuthContextValue {
  user: User | null
  loading: boolean
  error: string | null
  sendLoginLink: (email: string) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  getIdToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        setFirebaseUser(fbUser)
        try {
          const setupUser = httpsCallable(functions, 'setupUser')
          const result = await setupUser({ userEmail: fbUser.email })
          const data = result.data as { success: boolean; groups?: string[]; timezone?: string | null; language?: string | null; isSuperAdmin?: boolean }

          // Sync timezone from server profile on first login / when no local preference is set
          if (data.timezone && getTimezonePreference() === 'browser') {
            setTimezone(data.timezone)
          }
          // If user has a local timezone set but server doesn't have one yet, push it up
          if (!data.timezone && getTimezonePreference() !== 'browser') {
            const updateTz = httpsCallable(functions, 'updateUserTimezone')
            updateTz({ timezone: getTimezone() }).catch((err) => {
              console.error('Failed to sync timezone to profile:', err)
            })
          }

          // Sync language: server profile takes precedence if set
          const localLang = localStorage.getItem('edge_manager_language')
          if (data.language && data.language !== i18n.language) {
            i18n.changeLanguage(data.language)
          } else if (!data.language && localLang) {
            // Push local language to server if server doesn't have one yet
            const updateLang = httpsCallable(functions, 'updateUserLanguage')
            updateLang({ language: i18n.language }).catch((err) => {
              console.error('Failed to sync language to profile:', err)
            })
          }

          setUser({
            uid: fbUser.uid,
            email: fbUser.email,
            groups: data.groups || [],
            isSuperAdmin: data.isSuperAdmin || false,
          })
          setError(null)
        } catch (err) {
          console.error('Failed to setup user:', err)
          setUser({
            uid: fbUser.uid,
            email: fbUser.email,
            groups: []
          })
          setError(null)
        }
      } else {
        setFirebaseUser(null)
        setUser(null)
        setError(null)
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  // Handle email link sign-in on page load
  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = window.localStorage.getItem('emailForSignIn')
      if (!email) {
        email = window.prompt('Please provide your email for confirmation')
      }
      if (email) {
        signInWithEmailLink(auth, email, window.location.href)
          .then(() => {
            window.localStorage.removeItem('emailForSignIn')
            window.history.replaceState(null, '', window.location.pathname)
          })
          .catch((err) => {
            console.error('Email link sign-in error:', err)
            setError(err.message)
          })
      }
    }
  }, [])

  const sendLoginLink = useCallback(async (email: string) => {
    const actionCodeSettings = {
      url: `${window.location.origin}/login`,
      handleCodeInApp: true
    }

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings)
      window.localStorage.setItem('emailForSignIn', email)
      return { success: true }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send login link'
      setError(message)
      return { success: false, error: message }
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await signOut(auth)
    } catch (err) {
      console.error('Logout error:', err)
    }
  }, [])

  const getIdToken = useCallback(async () => {
    if (firebaseUser) {
      return firebaseUser.getIdToken()
    }
    return null
  }, [firebaseUser])

  return (
    <AuthContext.Provider value={{ user, loading, error, sendLoginLink, logout, getIdToken }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
